import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaWallet,
  FaArrowDown,
  FaArrowUp,
  FaHistory,
  FaCheckCircle,
  FaClock,
  FaTimesCircle,
  FaRupeeSign,
} from "react-icons/fa";

const AdvocateWallet = () => {
  const [walletData, setWalletData] = useState({
    balance: 0,
    transactions: [],
    user: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch wallet data (balance and transactions) for advocate users
  const fetchWalletData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/advocate/wallet/detail`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const responseData = response.data.data || response.data;
      const newWalletData = {
        balance: responseData.balance || 0,
        transactions: responseData.transactions || [],
        user: responseData.user || null,
      };

      setWalletData(newWalletData);
      setError(null);
    } catch (error) {
      console.error("Error fetching advocate wallet data:", error);
      setError("Failed to load wallet data");
      toast.error("Failed to load wallet information");
    }
  };

  useEffect(() => {
    const loadWalletData = async () => {
      setLoading(true);
      setError(null);
      try {
        await fetchWalletData();
      } finally {
        setLoading(false);
      }
    };
    loadWalletData();
  }, []);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "success":
      case "completed":
        return "text-green-600 bg-green-100";
      case "pending":
        return "text-yellow-600 bg-yellow-100";
      case "failed":
        return "text-red-600 bg-red-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const getTypeColor = (type) => {
    return type?.toLowerCase() === "credit"
      ? "text-green-600"
      : "text-red-600";
  };

  const getTypeIcon = (type) => {
    return type?.toLowerCase() === "credit"
      ? <FaArrowDown className="text-green-600" />
      : <FaArrowUp className="text-red-600" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading wallet...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* Header Section */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">Wallet</h1>
        <p className="text-sm sm:text-base text-gray-600">Manage your wallet balance and transaction history</p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg mb-6">
          <p className="font-medium">{error}</p>
        </div>
      )}

      {/* Wallet Balance Card */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl shadow-lg p-6 mb-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-orange-100 text-sm sm:text-base mb-2">Available Balance</p>
            <h2 className="text-3xl sm:text-4xl font-bold mb-1">
              ₹{walletData.balance?.toFixed(2) || "0.00"}
            </h2>
            {walletData.user && (
              <p className="text-orange-100 text-sm">
                {walletData.user.name} • {walletData.user.advocateId}
              </p>
            )}
          </div>
          <div className="bg-white/20 rounded-full p-4 sm:p-6">
            <FaWallet className="text-white text-3xl sm:text-4xl" />
          </div>
        </div>
      </div>

      {/* Transaction History Section */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                <FaHistory className="text-orange-500" />
                Transaction History
              </h2>
              <p className="text-gray-600 text-sm mt-1">
                All your wallet transactions
              </p>
            </div>
            <div className="text-sm text-gray-500">
              {walletData.transactions?.length || 0} Transaction{walletData.transactions?.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          {walletData.transactions && walletData.transactions.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {walletData.transactions.map((transaction, index) => (
                  <tr key={index} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatDate(transaction.date)}
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {getTypeIcon(transaction.type)}
                        <span className={`text-sm font-medium ${getTypeColor(transaction.type)}`}>
                          {transaction.type?.toUpperCase() || "N/A"}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 text-sm text-gray-700">
                      {transaction.description || "No description"}
                    </td>
                    <td className={`px-4 sm:px-6 py-4 whitespace-nowrap text-sm font-semibold ${getTypeColor(transaction.type)}`}>
                      {transaction.type?.toLowerCase() === "credit" ? "+" : "-"}
                      {formatAmount(Math.abs(transaction.amount || 0))}
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(transaction.status)}`}>
                        {transaction.status?.toUpperCase() || "PENDING"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-12 px-4">
              <FaHistory className="text-gray-300 text-5xl mx-auto mb-4" />
              <p className="text-gray-500 text-lg font-medium mb-2">No Transactions Yet</p>
              <p className="text-gray-400 text-sm">
                Your transaction history will appear here once you have any wallet activity.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Wallet Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mt-6">
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm mb-1">Total Credits</p>
              <p className="text-2xl font-bold text-green-600">
                ₹{walletData.transactions
                  ?.filter(t => t.type?.toLowerCase() === "credit")
                  .reduce((sum, t) => sum + (t.amount || 0), 0)
                  .toFixed(2) || "0.00"}
              </p>
            </div>
            <FaArrowDown className="text-green-600 text-2xl" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm mb-1">Total Debits</p>
              <p className="text-2xl font-bold text-red-600">
                ₹{walletData.transactions
                  ?.filter(t => t.type?.toLowerCase() === "debit")
                  .reduce((sum, t) => sum + (t.amount || 0), 0)
                  .toFixed(2) || "0.00"}
              </p>
            </div>
            <FaArrowUp className="text-red-600 text-2xl" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm mb-1">Total Transactions</p>
              <p className="text-2xl font-bold text-gray-800">
                {walletData.transactions?.length || 0}
              </p>
            </div>
            <FaHistory className="text-gray-600 text-2xl" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvocateWallet;

